import { Controller } from '@hotwired/stimulus';
import Axios from 'axios';

export default class extends Controller {
    static targets = ['input', 'feedback', 'tabLink', 'tabPane', 'formLoader'];

    initialize() {
        super.initialize();
        this.listeners = [];
        this.compress = this.element.dataset.compress === '1';
    }

    addListener(listener) {
        this.listeners.push(listener);
    }

    removeListener(listener) {
        const index = this.listeners.findIndex(controller => controller === listener);

        if (index >= 0) {
            this.listeners.splice(index, 1);
        }
    }

    change(event) {
        this.listeners.forEach((listener) => listener.fieldChanged(event.target));
    }

    async execute(event) {
        event.preventDefault();
        await this.handle(null);
    }

    deepSet(obj, path, value) {
        if (Object(obj) !== obj) {
            return obj;
        }

        if (!Array.isArray(path)) {
            path = path.toString().match(/[^.[\]]+/g) || [];
        }

        path.reduce((acc, curr, index) => {
            if (Object(acc[curr]) !== acc[curr]) {
                acc[curr] = Math.abs(path[index + 1]) >> 0 === +path[index + 1] ? [] : {};
            }

            if (index === path.length - 1) {
                acc[curr] = value;
            }

            return acc[curr];
        }, obj);

        return obj;
    }

    formDataObject(formData) {
        const root = {};
        for (const [path, value] of formData) {
            this.deepSet(root, path, value);
        }
        return root;
    }

    async handle(event) {
        if (this.isLoading()) {
            return;
        }

        this.showLoader();

        let response;

        const formData = new FormData(this.element);

        if (event && event.params) {
            for (const key in event.params) {
                formData.append(key, event.params[key]);
            }
        }

        const data = this.compress ? this.formDataObject(formData) : formData;

        try {
            response = await Axios[this.element.method](this.element.action, data);
        } catch (error) {
            console.error(error);
            response = error.response;
        }

        this.hideLoader();
        this.clearValidation();

        switch (response.status) {
            case 200:
                const passed = application.element.dispatchEvent(new CustomEvent('dashboard-form:success', {
                    bubbles: true,
                    cancelable: true,
                    detail: {
                        source: this.element
                    },
                }));

                if (passed) {
                    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
                        if (response.data.dashboard_toast) {
                            application.toastMessage(
                                response.data.dashboard_toast.dashboard_toast_header,
                                response.data.dashboard_toast.dashboard_toast_description,
                                response.data.dashboard_toast.dashboard_toast_timeout,
                                true,
                            );
                        }

                        if (response.data.reload_frame) {
                            this.showLoader();
                            const frame = document.getElementById(response.data.reload_frame);

                            if (frame) {
                                await frame.reload();
                            }

                            application.closeModals(response.data.close_modals);
                            this.hideLoader();
                        }

                        if (response.data.reload_page) {
                            this.showLoader();
                            Turbo.visit('');
                        }

                        if (response.data.redirect) {
                            this.showLoader();
                            Turbo.visit(response.data.redirect);
                        }

                        if (response.data.redirect_blank) {
                            application.closeModals();
                            let link = document.createElement('a');
                            link.href = response.data.redirect_blank;
                            link.target = '_blank';
                            link.click();
                        }

                        if (response.data.replace) {
                            this.showLoader();
                            Turbo.visit(response.data.replace, { action: "replace" });
                        }
                    } else {
                        if (response.headers['x-turbo-modal']) {
                            application.createModalOfContent(response.data, null);
                            return;
                        }

                        if (response.headers['x-turbo-modal-fresh']) {
                            await application.closeModals();
                            application.createModalOfContent(response.data, null);
                            return;
                        }
                    }
                }
                break;
            case 303:
                this.showLoader();
                location.href = response.data;
                break;
            case 403:
                this.hideLoader();

                if (response.data.dashboard_toast) {
                    application.toastMessage(
                        response.data.dashboard_toast.dashboard_toast_header,
                        response.data.dashboard_toast.dashboard_toast_description,
                        response.data.dashboard_toast.dashboard_toast_timeout,
                    );
                }
                break;
            case 404:
                this.hideLoader();
                application.toastGeneralError();
                break;
            case 422:
                this.hideLoader();
                this.validate(response.data.errors);

                if (response.data.errors.dashboard_toast) {
                    application.toastMessage(
                        response.data.errors.dashboard_toast.dashboard_toast_header,
                        response.data.errors.dashboard_toast.dashboard_toast_description,
                        response.data.errors.dashboard_toast.dashboard_toast_timeout,
                    );
                }
                break;
            case 500:
                this.hideLoader();
                application.toastGeneralError();
                break;
            default:
                this.hideLoader();
        }
    }

    isLoading() {
        return this.element.classList.contains('form-loading');
    }

    showLoader() {
        application.setLoading(true);
        this.element.classList.add('form-loading');
        this.formLoaderTargets.forEach(spinner => spinner.classList.remove('d-none'));
    }

    hideLoader() {
        application.setLoading(false);
        this.element.classList.remove('form-loading');
        this.formLoaderTargets.forEach(spinner => spinner.classList.add('d-none'));
    }

    clearValidation() {
        this.inputTargets.forEach(el => {
            el.classList.remove('is-invalid');
        });

        this.feedbackTargets.forEach(el => {
            el.classList.remove('d-block');
            el.classList.innerHTML = '';
        });

        this.tabLinkTargets.forEach(el => {
            el.classList.remove('is-invalid');
        });
    }

    validate(errors) {
        const invalidInputs = [];

        Object.entries(errors).forEach(([key, errors]) => {
            this.inputTargets.forEach(el => {
                if ((el.dataset.input || '') === key) {
                    el.classList.add('is-invalid');
                    invalidInputs.push(el);
                }
            });

            this.feedbackTargets.forEach(el => {
                if ((el.dataset.feedback || '') === key) {
                    el.classList.add('d-block');
                    el.innerHTML = errors[0] || '';
                }
            });
        });

        for (const tab of this.tabPaneTargets) {
            for (const input of invalidInputs) {
                if (tab.contains(input) && tab.hasAttribute('id')) {
                    const tabLink = this.element.querySelector("[href='#" + tab.getAttribute('id') + "']");

                    if (tabLink) {
                        tabLink.classList.add('is-invalid');
                    }

                    break;
                }
            }
        }

        for (const input of this.inputTargets) {
            if (input.classList.contains('is-invalid')) {
                input.focus();

                break;
            }
        }
    }
}
